import { Star } from "lucide-react";
import type { Review } from "@/data/reviews";

type ReviewVariant = "light" | "dark";
type StoryReviewContext = "editorial" | "hero" | "reassurance" | "proof" | "workflow";

export function ReviewCard({
  review,
  variant = "light",
  className = "",
}: {
  review: Review;
  variant?: ReviewVariant;
  className?: string;
}) {
  const card =
    variant === "light"
      ? "bg-white/90 backdrop-blur-[2px] border border-[#1A1A1A]/5 shadow-sm"
      : "bg-[#F2F2F3] border border-[#1A1A1A]/5";

  return (
    <article className={`rounded-[16px] p-[28px] sm:p-[32px] flex flex-col ${card} ${className}`}>
      <div className="flex gap-1 text-primary mb-[16px]" role="img" aria-label={`${review.rating} out of 5 stars`}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Star
            key={index}
            className={`w-4 h-4 ${index < review.rating ? "fill-current" : "text-[#1A1A1A]/15"}`}
          />
        ))}
      </div>
      {review.title && (
        <div className="text-[15px] font-black text-[#1A1A1A] mb-[8px] uppercase tracking-[0.02em]">
          {review.title}
        </div>
      )}
      <p className="m-0 text-[16px] leading-[1.55] font-medium text-[#1A1A1A] text-pretty flex-1">
        "{review.quote}"
      </p>
      <div className="mt-[20px] flex items-center gap-[12px]">
        <div className="w-[32px] h-[32px] rounded-full bg-[#1A1A1A] flex items-center justify-center text-white text-[12px] font-black uppercase shrink-0">
          {review.name.charAt(0)}
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-bold text-[#1A1A1A] uppercase tracking-wider truncate">
            {review.name}
          </div>
          {review.location && (
            <div className="text-[12px] font-medium text-[#666666] truncate">{review.location}</div>
          )}
        </div>
      </div>
    </article>
  );
}

export function StoryReview({
  review,
  variant = "light",
  context = "editorial",
  showMeta = true,
  accent = "primary",
  className = "",
}: {
  review: Review;
  variant?: ReviewVariant;
  context?: StoryReviewContext;
  showMeta?: boolean;
  accent?: "primary" | "white";
  className?: string;
}) {
  const dark = variant === "dark";
  const quoteColor = dark ? "text-white/85" : "text-[#1A1A1A]";
  const mutedColor = dark ? "text-white/60" : "text-[#666666]";
  const proofAccent = accent === "white" ? "border-white" : "border-primary";
  const contextClasses = {
    editorial: dark
      ? "border-l-2 border-primary pl-5 sm:pl-6"
      : "border-l-2 border-primary pl-5 sm:pl-6",
    hero: dark
      ? "border-t border-white/20 pt-4"
      : "border-t border-[#1A1A1A]/15 pt-4",
    reassurance: dark
      ? "border-t border-white/15 pt-4"
      : "border-t border-[#1A1A1A]/15 pt-4",
    proof: dark
      ? `border-t-2 ${proofAccent} pt-4`
      : `border-t-2 ${proofAccent} pt-4`,
    workflow: dark
      ? "border-l border-white/25 pl-4 sm:pl-5"
      : "border-l border-[#1A1A1A]/20 pl-4 sm:pl-5",
  }[context];
  return (
    <figure className={`m-0 min-w-0 ${contextClasses} ${className}`}>
      {showMeta && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="flex gap-0.5 text-primary" role="img" aria-label={`${review.rating} out of 5 stars`}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Star
                key={index}
                className={`h-3.5 w-3.5 ${index < review.rating ? "fill-current" : "text-[#1A1A1A]/15"}`}
              />
            ))}
          </div>
        </div>
      )}
      <blockquote className={`m-0 ${showMeta ? "mt-2.5" : "mt-0"} text-[15px] leading-[1.55] font-medium ${quoteColor}`}>
        “{review.quote}”
      </blockquote>
      <figcaption className={`mt-3 text-[11.5px] font-bold uppercase tracking-[0.08em] ${mutedColor}`}>
        {review.name}{review.location ? ` · ${review.location}` : ""}
      </figcaption>
    </figure>
  );
}

/**
 * Continuously rotating review carousel (marquee). The review list is
 * rendered twice so the loop is seamless; pass each page a disjoint set of
 * reviews so no review appears in more than one carousel.
 */
export default function ReviewsCarousel({
  reviews,
  variant = "light",
}: {
  reviews: Review[];
  variant?: ReviewVariant;
}) {
  return (
    <div className="marquee-container relative overflow-hidden" tabIndex={0} role="region" aria-label="Customer reviews">
      <div className="flex w-max animate-marquee pb-4">
        {[1, 2].map((group) => (
          <div key={group} className="flex gap-[24px] pr-[24px]" aria-hidden={group === 2}>
            {reviews.map((r, i) => (
              <ReviewCard
                key={i}
                review={r}
                variant={variant}
                className="w-[320px] sm:w-[420px] shrink-0"
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
